/**
 * Parses TSV (tab-separated) text respecting double-quoted fields.
 * Newlines and tabs inside "..." are treated as part of the field (e.g. for
 * Description with line breaks). Escaped quote "" becomes a single ".
 * @returns rows of columns (string[][])
 */
export function parseTSVWithQuotedFields(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let current = '';
  let i = 0;
  type State = 'field_start' | 'unquoted' | 'quoted';
  let state: State = 'field_start';

  while (i < text.length) {
    const c = text[i];

    if (state === 'field_start') {
      if (c === '"') {
        state = 'quoted';
        i++;
      } else if (c === '\t') {
        row.push('');
        i++;
      } else if (c === '\n') {
        rows.push(row);
        row = [];
        i++;
      } else if (c === '\r') {
        rows.push(row);
        row = [];
        i += i + 1 < text.length && text[i + 1] === '\n' ? 2 : 1;
      } else {
        current += c;
        state = 'unquoted';
        i++;
      }
    } else if (state === 'unquoted') {
      if (c === '\t') {
        row.push(current);
        current = '';
        state = 'field_start';
        i++;
      } else if (c === '\n') {
        row.push(current);
        rows.push(row);
        row = [];
        current = '';
        state = 'field_start';
        i++;
      } else if (c === '\r') {
        row.push(current);
        rows.push(row);
        row = [];
        current = '';
        state = 'field_start';
        i += i + 1 < text.length && text[i + 1] === '\n' ? 2 : 1;
      } else {
        current += c;
        i++;
      }
    } else {
      // quoted
      if (c === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '"';
          i += 2;
        } else {
          // Fine del campo quotato: push del valore e consumo immediato del separatore.
          // Senza questo, il \t successivo verrebbe gestito da field_start che farebbe
          // push('') generando una colonna vuota spuria dopo ogni campo quotato.
          row.push(current);
          current = '';
          i++; // avanza oltre la virgolette di chiusura
          if (i < text.length) {
            if (text[i] === '\t') {
              i++; // consuma il tab separatore senza push di campo vuoto
            } else if (text[i] === '\n') {
              rows.push(row);
              row = [];
              i++;
            } else if (text[i] === '\r') {
              rows.push(row);
              row = [];
              i += i + 1 < text.length && text[i + 1] === '\n' ? 2 : 1;
            }
          }
          state = 'field_start';
        }
      } else {
        current += c;
        i++;
      }
    }
  }

  if (current !== '' || row.length > 0) {
    row.push(current);
    rows.push(row);
  }
  return rows;
}

const INTERVENTION_TYPE_PASTE = new Set(['tecnologico', 'gestionale', 'procedure']);

/** Rimuove colonne vuote tipiche da export Excel: tra consumo e costo; tra descrizione e tipologia. */
export function normalizeInterventionsPasteRow(row: string[]): string[] {
  if (row.length < 7) return row;
  const r = [...row];
  const gap = String(r[5] ?? '').trim();
  const next = String(r[6] ?? '').trim();
  if (gap === '' && /^[\d.,-]+$/.test(next.replace(/\s/g, ''))) {
    r.splice(5, 1);
  }
  let collapsed: boolean;
  do {
    collapsed = false;
    for (let i = 7; i < r.length - 1; i++) {
      if (String(r[i] ?? '').trim() !== '') continue;
      const t = String(r[i + 1] ?? '')
        .trim()
        .toLowerCase();
      if (INTERVENTION_TYPE_PASTE.has(t)) {
        r.splice(i, 1);
        collapsed = true;
        break;
      }
    }
  } while (collapsed);
  return r;
}
